import { m } from 'framer-motion';
import { useState, useRef } from 'react';
// material
import { alpha, styled, useTheme } from '@mui/material/styles';
import { Box, Typography, Card } from '@mui/material';
import { isNilOrBlank } from '../../utils/core';

import Image from '../image';
import Lightbox from '../lightbox/Lightbox';
import Carousel, { CarouselArrowIndex } from '../carousel';

import { MotionContainer, varFade } from '../animate';
import useLocales from '../../hooks/useLocales';

import { useSelector } from '../../redux/store';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(() => ({
  width: '100%'
}));

// ----------------------------------------------------------------------

export default function FrontEndMainEntityDisplay() {
  const theme = useTheme();
  const carouselRef = useRef(null);
  const { translate: t } = useLocales();
  const { activeLeaf, levelPictures } = useSelector((state) => state.frontend);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openLightbox, setOpenLightbox] = useState(false);
  const [selectedImage, setSelectedImage] = useState(0);
  const basicCarouselSettings = {
    dots: false,
    arrows: false,
    autoplay: true,
    slidesToShow: 1,
    slidesToScroll: 1,
    rtl: Boolean(theme.direction === 'rtl')
  };
  const leafCarouselSettings = {
    ...basicCarouselSettings,
    beforeChange: (current, next) => setCurrentIndex(next)
  };

  const levelCarouselSettings = {
    ...basicCarouselSettings,
    fade: true,
    infinite: true,
    speed: 5000
  };

  const handlePrev = () => {
    carouselRef.current?.slickPrev();
  };

  const handleNext = () => {
    carouselRef.current?.slickNext();
  };

  const handleOpenLightbox = (url) => {
    const selectedImage = levelPictures.findIndex((index) => index === url);
    setOpenLightbox(true);
    setSelectedImage(selectedImage);
  };

  const handleCloseLightbox = () => {
    setOpenLightbox(false);
  };

  return (
    <>
      {levelPictures.length > 0 && isNilOrBlank(activeLeaf) && (
        <MotionContainer>
          <RootStyle>
            <m.div viewport={{ once: true }} variants={varFade().inLeft}>
              <Box
                sx={{
                  borderRadius: 1.5,
                  // padding: 1,
                  backgroundColor: (theme) => alpha(theme.palette.grey[200], 0.95),
                  mt: 1
                }}
              >
                <Card>
                  <Carousel ref={carouselRef} {...levelCarouselSettings}>
                    {levelPictures.map((picture, pi) => (
                      <Image
                        key={`entity-picture-level-${pi}`}
                        onClick={() => handleOpenLightbox(picture)}
                        alt={''}
                        src={picture}
                        ratio="16/9"
                      />
                    ))}
                  </Carousel>
                </Card>
              </Box>
            </m.div>
          </RootStyle>
          <Lightbox
            images={levelPictures}
            mainSrc={levelPictures[selectedImage]}
            photoIndex={selectedImage}
            setPhotoIndex={setSelectedImage}
            open={openLightbox}
            onCloseRequest={handleCloseLightbox}
          />
        </MotionContainer>
      )}
      {!isNilOrBlank(activeLeaf) && (
        <MotionContainer>
          <RootStyle>
            <m.div viewport={{ once: true }} variants={varFade().inLeft}>
              <Box
                sx={{
                  borderRadius: 1.5,
                  // padding: 1,
                  backgroundColor: (theme) => alpha(theme.palette.grey[200], 0.95),
                  mt: 1
                }}
              >
                {!isNilOrBlank(activeLeaf.pictures) && activeLeaf.pictures.length > 0 && (
                  <Card>
                    <Carousel ref={carouselRef} {...leafCarouselSettings}>
                      {activeLeaf.pictures.map((picture) => (
                        <Image
                          key={`entity-picture-${activeLeaf.id}`}
                          alt={activeLeaf.full_name}
                          src={picture}
                          ratio="16/9"
                        />
                      ))}
                    </Carousel>

                    {activeLeaf.pictures.length > 1 && (
                      <CarouselArrowIndex
                        index={currentIndex}
                        total={activeLeaf.pictures.length}
                        onNext={handleNext}
                        onPrevious={handlePrev}
                      />
                    )}
                  </Card>
                )}
                <Box
                  sx={{
                    padding: 1
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    color={
                      !isNilOrBlank(activeLeaf.group) && !isNilOrBlank(activeLeaf.group.color)
                        ? activeLeaf.group.color
                        : theme.palette.primary.main
                    }
                  >
                    {activeLeaf.full_name}
                  </Typography>
                  <Typography component="div" variant="caption">
                    <strong>{t('home.entityType')}:</strong> {activeLeaf.group_name}
                  </Typography>
                  {!isNilOrBlank(activeLeaf.address) && activeLeaf.address !== '' && (
                    <Typography component="div" variant="caption">
                      <strong>{t('home.entityAddress')}:</strong> {activeLeaf.address}
                    </Typography>
                  )}
                  {!isNilOrBlank(activeLeaf.contact_person) && activeLeaf.contact_person !== '' && (
                    <Typography component="div" variant="caption">
                      <strong>{t('home.entityContactPerson')}: </strong>
                      {activeLeaf.contact_person}
                    </Typography>
                  )}
                  {!isNilOrBlank(activeLeaf.phone_number) && activeLeaf.phone_number !== '' && (
                    <Typography component="div" variant="caption">
                      <strong>{t('home.entityPhoneNumber')}:</strong> {activeLeaf.phone_number}
                    </Typography>
                  )}
                  {!isNilOrBlank(activeLeaf.email) && activeLeaf.email !== '' && (
                    <Typography component="div" variant="caption">
                      <strong>{t('home.entityContactEmail')}: </strong> {activeLeaf.email}
                    </Typography>
                  )}{' '}
                </Box>
              </Box>
            </m.div>
          </RootStyle>
        </MotionContainer>
      )}
    </>
  );
}
